import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getAvailableWorkspaces } from '../utils/workspaces';

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 20px 32px;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #111827;
`;

const Hello = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: center;
`;

const Sub = styled.p`
  margin: 8px 0 36px;
  color: #6b7280;
  font-size: 15px;
  text-align: center;
`;

const Grid = styled.div`
  width: 100%;
  max-width: 860px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;
`;

const Card = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  animation: ${rise} 0.35s ease both;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  &:hover, &:focus-visible {
    transform: translateY(-3px);
    border-color: ${(p) => p.$accent};
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.10);
  }
  &:focus-visible { outline: 2px solid ${(p) => p.$accent}; outline-offset: 2px; }
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$tint};
  color: ${(p) => p.$accent};
`;

const CardTitle = styled.div`
  font-size: 17px;
  font-weight: 650;
`;

const CardText = styled.div`
  font-size: 13.5px;
  line-height: 1.5;
  color: #6b7280;
`;

const Open = styled.div`
  margin-top: auto;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.$accent};
`;

const Foot = styled.button`
  all: unset;
  cursor: pointer;
  margin-top: 36px;
  font-size: 13px;
  color: #6b7280;
  &:hover { color: #dc2626; text-decoration: underline; }
`;

const Msg = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
`;

const svgProps = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };

const ICONS = {
  admin: (
    <svg {...svgProps}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>
  ),
  lecturer: (
    <svg {...svgProps}><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c3 2 9 2 12 0v-5" /></svg>
  ),
  adminSales: (
    <svg {...svgProps}><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>
  ),
  recovery: (
    <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M14.5 9.2c-.4-.7-1.3-1.2-2.5-1.2-1.4 0-2.5.8-2.5 1.9s1 1.6 2.5 2.1 2.5 1 2.5 2.1-1.1 1.9-2.5 1.9c-1.2 0-2.1-.5-2.5-1.2" /><path d="M12 6.5V8m0 8v1.5" /></svg>
  ),
  lab: (
    <svg {...svgProps}><path d="M9 3h6" /><path d="M10 3v6L4.5 19a1.5 1.5 0 001.3 2.2h12.4a1.5 1.5 0 001.3-2.2L14 9V3" /><path d="M7.5 15h9" /></svg>
  ),
  tvec: (
    <svg {...svgProps}><path d="M12 3l8 3v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-3z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></svg>
  ),
  sales: (
    <svg {...svgProps}><path d="M3 4h2l2.4 11.2a1.5 1.5 0 001.5 1.2h8.6a1.5 1.5 0 001.5-1.1L21 8H6" /><circle cx="9.5" cy="20" r="1.2" /><circle cx="17" cy="20" r="1.2" /></svg>
  ),
};

export default function WorkspaceSelect() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const stored = AuthService.getUser();
  const name = stored?.superAdmin?.name || stored?.superAdmin?.staff?.name || '';

  useEffect(() => {
    if (!stored?.apiKey) {
      navigate('/login', { replace: true });
      return undefined;
    }
    let cancelled = false;
    getAvailableWorkspaces(stored)
      .then((list) => {
        if (cancelled) return;
        // Nothing to choose between: go straight in (or fall back to admin).
        if (list.length <= 1) {
          navigate(list[0]?.path || '/admin/dashboard', { replace: true });
          return;
        }
        setItems(list);
      })
      .catch(() => { if (!cancelled) navigate('/admin/dashboard', { replace: true }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try { await axiosInstance.post('/logout'); } catch (_) { /* ignore */ }
    AuthService.clear();
    window.location.href = '/';
  };

  if (!items) return <Msg>Loading your workspaces…</Msg>;

  return (
    <Page>
      <Hello>Welcome back{name ? `, ${name.split(' ')[0]}` : ''}</Hello>
      <Sub>Choose where you want to work. You can switch any time from the menu in the top right.</Sub>
      <Grid>
        {items.map((w, i) => (
          <Card
            key={w.key}
            $accent={w.accent}
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={() => navigate(w.path)}
          >
            <IconWrap $tint={w.tint} $accent={w.accent}>{ICONS[w.key]}</IconWrap>
            <div>
              <CardTitle>{w.title}</CardTitle>
              <CardText>{w.description}</CardText>
            </div>
            <Open $accent={w.accent}>Open →</Open>
          </Card>
        ))}
      </Grid>
      <Foot onClick={signOut}>Sign out</Foot>
    </Page>
  );
}
